import { useNavigate, useParams } from "react-router-dom";
import { WorkflowRuleBuilder } from "../components/WorkflowRuleBuilder";
import { WorkflowStepBuilder } from "../components/WorkflowStepBuilder";
import { WorkflowStatusBadges } from "../components/WorkflowStatusBadges";
import { WorkflowToolbar } from "../components/WorkflowToolbar";
import { useWorkflow, usePublishWorkflow } from "../hooks";
import { moduleLabel } from "../utils";

export default function WorkflowDetailsPage() {
  const { workflowId } = useParams();
  const id = Number(workflowId);
  const navigate = useNavigate();

  const { data: workflow, isLoading, error } = useWorkflow(id);

  if (isLoading) return <p className="p-6">Loading workflow...</p>;

  if (error || !workflow) {
    return <p className="p-6 text-red-600">Failed to load workflow.</p>;
  }

  return (
    <div className="space-y-6 p-6">
      <button
        onClick={() => navigate("/admin/workflows")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
      >
        ← Back to Workflows
      </button>

      <div className="rounded-2xl border bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {workflow.name}
            </h1>
            <p className="text-sm font-semibold text-emerald-700">
              {workflow.code} • {moduleLabel(workflow.moduleCode)}
            </p>
            <p className="mt-2 max-w-3xl text-sm text-slate-500">
              {workflow.description || "No description provided."}
            </p>
          </div>

          <WorkflowToolbar workflow={workflow} />
        </div>

        <div className="mt-4">
          <WorkflowStatusBadges workflow={workflow} />
        </div>
      </div>

      {!workflow.published && <UnpublishedBanner workflow={workflow} />}

      <WorkflowStepBuilder workflow={workflow} />

      <WorkflowRuleBuilder workflow={workflow} />
    </div>
  );
}

// Editing a step or rule always unpublishes the workflow on the backend (a deliberate safety
// gate — half-edited approval logic never goes live by accident). But that also means a
// workflow that WAS live and routing real approvals can silently stop doing so the moment
// someone tweaks it, with only a small status pill to notice. This banner makes that
// impossible to miss, and lets them fix it in one click without hunting for the toolbar button.
function UnpublishedBanner({ workflow }: { workflow: { id: number; active: boolean } }) {
  const publish = usePublishWorkflow();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4">
      <div>
        <p className="text-sm font-bold text-amber-900">
          This workflow is unpublished — it will NOT route any approvals right now.
        </p>
        <p className="mt-0.5 text-xs text-amber-700">
          New requests in this module will fail to find this workflow until you publish it again.
          Any step or rule change unpublishes automatically, even a small tweak.
        </p>
      </div>
      <button
        type="button"
        disabled={!workflow.active || publish.isPending}
        onClick={() => publish.mutate(workflow.id)}
        className="shrink-0 rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
      >
        {publish.isPending ? "Publishing…" : "Publish now"}
      </button>
    </div>
  );
}
